import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { SignOut, Envelope, TrendUp, Users, CheckCircle, Trash } from "@phosphor-icons/react";

export default function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [user, loading, navigate]);

  const refresh = async () => {
    setBusy(true);
    try {
      const [l, s] = await Promise.all([api.get("/admin/leads"), api.get("/admin/stats")]);
      setLeads(l.data);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user) refresh();
  }, [user]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success(`Lead marked ${status}`);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead deleted");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] text-zinc-500 font-mono text-xs">
        Loading…
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505] text-white">
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-60 bg-[#07070A] border-r border-ink-600 text-zinc-100 flex-col justify-between p-6">
        <div>
          <Link to="/" className="flex items-center gap-3 group">
            <span className="relative flex items-center justify-center w-7 h-7 border border-white/20 group-hover:border-[#00F0FF] transition-colors">
              <span className="absolute inset-1 bg-[#00F0FF] shadow-[0_0_18px_2px_rgba(0,240,255,0.55)]" />
              <span className="absolute -right-1 -bottom-1 w-2 h-2 bg-[#00F0FF] pulse-dot" />
            </span>
            <span className="font-display text-base font-black tracking-tight">
              HUMINT<span className="text-cy">.</span>
            </span>
          </Link>
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500 mt-1">
            Admin console
          </div>
          <nav className="mt-10">
            <div className="block bg-[#0B0B10] border-l-2 border-cy pl-3 py-2 text-sm">Leads</div>
          </nav>
        </div>
        <div>
          <div className="text-xs text-zinc-500 mb-2">{user.email}</div>
          <button
            onClick={() => {
              logout();
              navigate("/admin/login");
            }}
            data-testid="admin-logout"
            className="w-full inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-cy transition-colors"
          >
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      <main className="md:pl-60">
        <div className="max-w-[1280px] px-6 md:px-10 py-10 md:py-14">
          <div className="flex items-start md:items-center justify-between flex-col md:flex-row gap-4 mb-10">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-cy">
                humint.admin / leads
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mt-2 text-white">
                Leads inbox.
              </h1>
            </div>
            <button
              onClick={refresh}
              data-testid="admin-refresh"
              className="border border-ink-500 text-white px-4 py-2 text-xs font-mono uppercase tracking-[0.2em] hover:bg-cy hover:text-ink transition-colors"
            >
              Refresh
            </button>
          </div>

          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-ink-500 mb-10">
              <Stat icon={Users} label="Total leads" value={stats.total_leads} />
              <Stat icon={Envelope} label="New" value={stats.new_leads} accent />
              <Stat icon={CheckCircle} label="Contacted" value={stats.contacted_leads} />
              <Stat icon={TrendUp} label="Last 7 days" value={stats.leads_last_7_days} />
            </div>
          )}

          <div className="border border-ink-500 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-500 bg-[#0A0A0D]">
                  <Th>Received</Th>
                  <Th>Name</Th>
                  <Th>Company</Th>
                  <Th>Contact</Th>
                  <Th>Service</Th>
                  <Th>Challenge</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {busy && (
                  <tr>
                    <td colSpan={8} className="p-6 text-center text-zinc-500">
                      Loading leads…
                    </td>
                  </tr>
                )}
                {!busy && leads.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-6 text-center text-zinc-500">
                      No leads yet.
                    </td>
                  </tr>
                )}
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    className="border-b border-ink-500 hover:bg-white/[0.02]"
                    data-testid={`lead-row-${l.id}`}
                  >
                    <Td mono>{l.created_at?.slice(0, 10)}</Td>
                    <Td bold>{l.name}</Td>
                    <Td>{l.company || "—"}</Td>
                    <Td>
                      <div className="text-white">{l.email}</div>
                      {l.phone && <div className="text-xs text-zinc-500">{l.phone}</div>}
                    </Td>
                    <Td>{l.service_interest || "—"}</Td>
                    <Td>
                      <div className="max-w-xs truncate" title={l.challenge}>
                        {l.challenge}
                      </div>
                    </Td>
                    <Td>
                      <StatusPill status={l.status} />
                    </Td>
                    <Td>
                      <div className="flex gap-1">
                        <ActBtn onClick={() => updateStatus(l.id, "contacted")} testid={`mark-contacted-${l.id}`}>
                          Contacted
                        </ActBtn>
                        <ActBtn onClick={() => updateStatus(l.id, "closed")} testid={`mark-closed-${l.id}`}>
                          Closed
                        </ActBtn>
                        <button
                          onClick={() => remove(l.id)}
                          data-testid={`delete-lead-${l.id}`}
                          className="p-1.5 text-zinc-500 hover:text-cy transition-colors"
                          aria-label="Delete"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

const Th = ({ children }) => (
  <th className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 px-4 py-3">
    {children}
  </th>
);
const Td = ({ children, mono, bold }) => (
  <td
    className={`px-4 py-3 align-top ${mono ? "font-mono text-xs text-zinc-400" : "text-zinc-300"} ${
      bold ? "font-semibold text-white" : ""
    }`}
  >
    {children}
  </td>
);
const ActBtn = ({ children, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 border border-ink-500 text-zinc-300 hover:border-cy hover:text-cy transition-colors"
  >
    {children}
  </button>
);

function StatusPill({ status }) {
  const map = {
    new: "bg-cy text-ink",
    contacted: "bg-white text-ink",
    closed: "bg-ink-500 text-zinc-400",
  };
  return (
    <span
      className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${
        map[status] || "bg-ink-500 text-zinc-400"
      }`}
    >
      {status}
    </span>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="border-r border-b border-ink-500 p-6 bg-[#07070A]">
      <div className="flex items-start justify-between">
        <Icon size={20} weight="duotone" className={accent ? "text-cy" : "text-white"} />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
      </div>
      <div className="font-display text-4xl font-black tracking-tight mt-6 text-white">{value ?? 0}</div>
    </div>
  );
}
